import { createContext, useContext, useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Crown } from "lucide-react";

export const DonateContext = createContext({ openDonate: () => {} });
export const useDonate = () => useContext(DonateContext);

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 34 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, className = "" }) => (
  <p
    className={`text-xs sm:text-sm uppercase tracking-[0.3em] text-gold font-medium ${className}`}
  >
    {children}
  </p>
);

export const SectionHeading = ({ eyebrow, title, sub, align = "center", dark = false, chapter }) => {
  const alignCls = align === "left" ? "text-left items-start" : "text-center items-center";
  return (
    <Reveal className={`flex flex-col gap-5 ${alignCls}`}>
      {chapter ? (
        <div className="flex items-center gap-4">
          <span className="font-serif italic text-3xl sm:text-4xl gold-text leading-none">{chapter}</span>
          <span className="h-px w-10 sm:w-16 bg-champagne/40" />
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        </div>
      ) : (
        eyebrow && <Eyebrow>{eyebrow}</Eyebrow>
      )}
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ivory leading-[1.08] max-w-4xl">
        {title}
      </h2>
      {sub && (
        <p className={`text-base sm:text-lg leading-relaxed max-w-2xl ${dark ? "text-cream/80" : "text-cream"}`}>
          {sub}
        </p>
      )}
      <div className="h-px w-24 bg-gradient-to-r from-transparent via-gold to-transparent" />
    </Reveal>
  );
};

export const Marquee = ({ items, className = "" }) => {
  const row = [...items, ...items, ...items];
  return (
    <div
      className={`relative overflow-hidden border-y border-champagne/15 bg-coal/60 py-5 sm:py-7 ${className}`}
      aria-hidden="true"
    >
      <div className="marquee-track flex w-max items-center whitespace-nowrap">
        {row.map((item, i) => (
          <span key={i} className="flex items-center">
            <span
              className={`font-serif text-2xl sm:text-4xl tracking-wide px-6 sm:px-10 ${
                i % 2 === 0 ? "text-ivory/90" : "text-stroke-gold italic"
              }`}
            >
              {item}
            </span>
            <Crown size={18} className="text-gold/60 shrink-0" />
          </span>
        ))}
      </div>
    </div>
  );
};

export const GoldButton = ({
  children,
  href,
  onClick,
  variant = "solid",
  testid,
  className = "",
  external = false,
}) => {
  const base =
    "inline-flex items-center justify-center gap-2 uppercase tracking-[0.18em] text-xs sm:text-sm font-semibold px-8 py-4 rounded-full transition-[transform,box-shadow,background-color,color,border-color] duration-300 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer";
  const styles = {
    solid:
      "bg-gradient-to-r from-gold via-champagne to-gold text-ink shadow-[0_10px_35px_-10px_rgba(230,199,134,0.55)] hover:shadow-[0_16px_45px_-8px_rgba(230,199,134,0.7)]",
    outline:
      "border border-champagne/50 text-champagne hover:bg-champagne/10 hover:border-champagne",
    ghost: "text-cream hover:text-champagne",
    wine: "bg-wine text-ivory border border-winelight hover:bg-winelight",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  if (href) {
    return (
      <a
        data-testid={testid}
        href={href}
        onClick={onClick}
        className={cls}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {children}
      </a>
    );
  }
  return (
    <button data-testid={testid} type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
};

export const CountUp = ({ value, prefix = "", suffix = "", duration = 1.8, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v).toLocaleString("en-US")),
    });
    return () => controls.stop();
  }, [inView, value, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export const Thermometer = ({
  goal,
  raised,
  milestones,
  accent = "gold",
  testidPrefix = "thermometer",
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const pct = Math.min(100, goal > 0 ? (raised / goal) * 100 : 0);
  const fill =
    accent === "neon"
      ? "bg-gradient-to-r from-magenta via-neon to-magenta"
      : "bg-gradient-to-r from-gold via-champagne to-gold";
  const glow =
    accent === "neon"
      ? "shadow-[0_0_24px_rgba(0,229,255,0.45)]"
      : "shadow-[0_0_24px_rgba(230,199,134,0.4)]";

  return (
    <div ref={ref} className="w-full" data-testid={`${testidPrefix}-container`}>
      <div className="relative h-5 sm:h-6 rounded-full bg-white/5 border border-white/10 overflow-visible">
        <motion.div
          className={`absolute left-0 top-0 bottom-0 rounded-full ${fill} ${glow}`}
          initial={{ width: "0%" }}
          animate={inView ? { width: `${Math.max(pct, 1.2)}%` } : {}}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`${testidPrefix}-progress-bar`}
        />
        {milestones.map((m) => (
          <div
            key={m.value}
            className="absolute top-1/2 -translate-y-1/2"
            style={{ left: `${(m.value / goal) * 100}%` }}
          >
            <div className={`w-px h-8 sm:h-9 -translate-x-1/2 ${accent === "neon" ? "bg-neon/40" : "bg-champagne/40"}`} />
            <span
              className={`absolute top-9 sm:top-10 -translate-x-1/2 text-[10px] sm:text-xs tracking-widest whitespace-nowrap ${
                accent === "neon" ? "text-neon/80" : "text-cream/70"
              }`}
            >
              {m.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
